import * as React from "react";
import { FormattedMessage } from "react-intl";
// components
import { Link } from "react-router-dom";
import PlaceHolder from "./placeHolder";
import Icon, { IconWrapper } from "common/icons";
import PACKS from "common/icons/packs";
import Popover from "@material-ui/core/Popover";
import SolidButton from "common/components/solidButton";
import FullscreenDialog, {
  DIALOG_TYPES,
} from "common/components/fullscreenDialog";
import ListTitle from "common/components/listTitle";
import PulseLoadingDot from "common/components/pulseLoadingDot";
import UserList, { Type as UserListType } from "common/components/userList";
import EmptyMessage from "common/components/emptyMessage";
import RoundImage from "common/components/roundImage";
// helpers
import NumberFormatter from "@vingle/wtf-sdk/components/numberFormatter";
import { BLUE700, WHITE100 } from "@vingle/wtf-sdk/constants/palette";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { UserBlockLevel } from "@vingle/wtf-sdk/enums";
// styles

const styles = require("./profileDefaultHeader.scss");

interface IProps {
  isMine: boolean;
  currentUser: Vingle.User.ICurrentUser | null;
  user?: Vingle.User.IUser;
  userProfile: Vingle.UserProfile.IProfileUserState;
  scoreState: { [key: string]: Vingle.IPaginatedList<Vingle.User.IUser> };
  blockedStatus?: Vingle.UserProfile.IBlockResponseData;
  onDMClick: () => void;
  onFollowThisProfileUser: () => void;
  onUnFollowThisProfileUser: () => void;
  onFollowUserOfList: (user: Vingle.User.IListUser) => void;
  onFullScreenInfiniteLoad: (type: DIALOG_TYPES) => void;
  onInitialUserListLoad: (type: DIALOG_TYPES) => void;
  onSetUserBlockStatus: (
    username: string,
    blockLevel: Vingle.Enums.UserBlockLevel,
  ) => void;
  onChangeUserProfile?: (params: {
    updateFields: Vingle.User.IUserForm;
    successMessage?: string;
    profile_image?: File | Blob | null;
    cover_image?: File | Blob;
  }) => void;
}

interface IState {
  dialogType: DIALOG_TYPES | null;
  isDialogOpen: boolean;
  profile_image: string | null;
  cover_image: string | null;
  isOpenDropdownMenu: boolean;
  anchorOfDropdownMenu: any;
}

class ProfileDefaultHeader extends React.PureComponent<IProps, IState> {
  public state: IState = {
    isDialogOpen: false,
    dialogType: null,
    profile_image: null,
    cover_image: null,
    isOpenDropdownMenu: false,
    anchorOfDropdownMenu: null,
  };

  private readonly fileInput: React.RefObject<
    HTMLInputElement
  > = React.createRef();

  public render() {
    const { user } = this.props;

    if (!user) {
      return <PlaceHolder />;
    }

    return (
      <div className={styles.headerWrapper}>
        <div className={styles.wallPaper}>
          {this.getCoverImageDOM()}
          {this.renderChangePhoto("cover-image")}
        </div>
        <div className={styles.userInfoWrapper}>
          <div className={styles.profileImageWrapper}>
            {this.getProfileImageDOM()}
            {this.renderChangePhoto()}
          </div>
          <div className={styles.leftWrapper}>
            <div className={styles.userInfo}>
              <div className={styles.usernameWrapper}>
                <span className={styles.username}>
                  <Link
                    to={new VingleURL.UserShow({
                      username: user.username,
                    }).toString()}
                  >
                    {user.username}
                  </Link>
                </span>
                <span className={styles.traitIconWrapper}>
                  {this.getTraitIcon()}
                </span>
                {this.renderPopoverIcon()}
                {this.getPopover()}
              </div>
              {this.getUserDescription()}
            </div>
            {this.getHeaderButton()}
          </div>

          <div className={styles.userScoreWrapper}>
            <div
              className={styles.scoreItem}
              onClick={this.handleRequestFolloweeDialogOpen}
            >
              <div className={styles.score}>
                {<NumberFormatter num={user.followee_count || 0} digits={1} />}
              </div>
              <div className={styles.scoreTitle}>
                <FormattedMessage id={"profile.following_text"} />
              </div>
            </div>
            <div className={styles.rightDivide} />
            <div
              className={styles.scoreItem}
              onClick={this.handleRequestFollowerDialogOpen}
            >
              <div className={styles.score}>
                {<NumberFormatter num={user.follower_count || 0} digits={1} />}
              </div>
              <div className={styles.scoreTitle}>
                <FormattedMessage id={"profile.follower_text"} />
              </div>
            </div>
            <div className={styles.rightDivide} />
            <div
              className={styles.scoreItem}
              onClick={this.handleRequestBoosterDialogOpen}
            >
              <div className={styles.score}>
                {<NumberFormatter num={user.boost_count || 0} digits={1} />}
              </div>
              <div className={styles.scoreTitle}>
                <FormattedMessage id={"profile.boost_text"} />
              </div>
            </div>
          </div>
        </div>
        {this.renderUserListDialog()}
        <input
          ref={this.fileInput}
          style={{ display: "none" }}
          type="file"
          accept="image/*"
          onChange={this.handleChangeImage}
        />
      </div>
    );
  }

  private readonly handlePopoverClick = (
    e: React.MouseEvent<HTMLSpanElement>,
  ) => {
    this.handleOpenDropdown(e.currentTarget);
  };

  private readonly renderPopoverIcon = () => {
    const { isMine, blockedStatus } = this.props;
    if (isMine || !blockedStatus) {
      return null;
    } else {
      return (
        <span className={styles.userMenu} onClick={this.handlePopoverClick}>
          <Icon icon="DOWN_ARROW" />
        </span>
      );
    }
  };

  private readonly getUsername = () => {
    const { user } = this.props;
    if (user) {
      return user.username;
    } else {
      return null;
    }
  };

  private readonly handleToggleBlock = () => {
    const username = this.getUsername();
    if (username) {
      this.props.onSetUserBlockStatus(username, UserBlockLevel.BLOCKED);
      this.handleCloseDropdown();
    }
  };

  private readonly handleToggleMute = () => {
    const username = this.getUsername();
    if (username) {
      this.props.onSetUserBlockStatus(username, UserBlockLevel.MUTED);
      this.handleCloseDropdown();
    }
  };

  private readonly getPopover = () => {
    const { isMine, blockedStatus } = this.props;
    const blockUserMessageId = "profile.header.block_user";

    if (isMine) {
      return null;
    }
    if (!blockedStatus) {
      return null;
    }

    let blockButtons;
    switch (blockedStatus.level) {
      case UserBlockLevel.NORMAL:
        blockButtons = [
          <li
            onClick={this.handleToggleMute}
            key={`blockLevel_${blockedStatus.level}_${UserBlockLevel.NORMAL}`}
          >
            <FormattedMessage id={"profile.header.hide_user"} />
          </li>,
          <li
            onClick={this.handleToggleBlock}
            key={`blockLevel_${blockedStatus.level}_${UserBlockLevel.BLOCKED}`}
          >
            <FormattedMessage id={blockUserMessageId} />
          </li>,
        ];
        break;
      case UserBlockLevel.MUTED:
        blockButtons = [
          <li
            onClick={this.handleToggleMute}
            key={`blockLevel_${blockedStatus.level}_${UserBlockLevel.NORMAL}`}
          >
            <FormattedMessage id={"profile.header.unhide_user"} />
          </li>,
          <li
            onClick={this.handleToggleBlock}
            key={`blockLevel_${blockedStatus.level}_${UserBlockLevel.BLOCKED}`}
          >
            <FormattedMessage id={blockUserMessageId} />
          </li>,
        ];
        break;
      case UserBlockLevel.BLOCKED:
        blockButtons = [
          <li
            onClick={this.handleToggleBlock}
            key={`blockLevel_${blockedStatus.level}_${UserBlockLevel.NORMAL}`}
          >
            <FormattedMessage id={"profile.header.unblock_user"} />
          </li>,
        ];
        break;
      default:
        blockButtons = [
          <li
            onClick={this.handleToggleMute}
            key={`blockLevel_${blockedStatus.level}_${UserBlockLevel.NORMAL}`}
          >
            <FormattedMessage id={"profile.header.hide_user"} />
          </li>,
          <li
            onClick={this.handleToggleBlock}
            key={`blockLevel_${blockedStatus.level}_${UserBlockLevel.MUTED}`}
          >
            <FormattedMessage id={blockUserMessageId} />
          </li>,
        ];
    }

    return (
      <Popover
        classes={{ paper: styles.profilePopoverWrapper }}
        open={this.state.isOpenDropdownMenu}
        anchorEl={this.state.anchorOfDropdownMenu}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
        onClose={this.handleCloseDropdown}
        elevation={2}
        transitionDuration={0}
      >
        {blockButtons}
      </Popover>
    );
  };

  private readonly getUserDescription = () => {
    const { isMine, user } = this.props;

    if (!isMine && user && user.reverse_relation) {
      return (
        <div className={styles.userDescription}>
          {user.reverse_relation.summary}
        </div>
      );
    } else {
      return null;
    }
  };

  private readonly getTraitIcon = () => {
    const { user } = this.props;

    switch (user && user.trait) {
      case "star": {
        return <Icon icon="USER_FOLLOW_USER_TYPE_STAR" />;
      }
      case "verify": {
        return <Icon icon="USER_FOLLOW_USER_TYPE_VERIFY" />;
      }
      default: {
        return null;
      }
    }
  };

  private readonly getProfileImageDOM = () => {
    const { user } = this.props;

    if (this.state.profile_image) {
      return (
        <div
          style={{ backgroundImage: `url("${this.state.profile_image}")` }}
          className={styles.profileImage}
        />
      );
    } else if (user && user.profile_image_urls) {
      return (
        <RoundImage
          imageSrc={user.profile_image_urls}
          overlayClassName={styles.profileImageBorder}
          width={116}
          height={116}
        />
      );
    } else {
      return <div className={styles.placeHolderImage} />;
    }
  };

  private readonly renderChangePhoto = (type?: string) => {
    if (this.props.isMine) {
      const handleClick = () => {
        this.handleChangePhoto(type);
      };
      return (
        <div className={styles.changePhoto} onClick={handleClick}>
          <IconWrapper icon={PACKS.PROFILE.CHANGE_PHOTO} />
          {type ? (
            <FormattedMessage id="user_edit.change_profile_image" />
          ) : null}
        </div>
      );
    }
  };

  private readonly getUserFollowUnFollowButton = () => {
    const {
      onFollowThisProfileUser,
      onUnFollowThisProfileUser,
      user,
    } = this.props;
    if (user) {
      if (user.relation && user.relation.following) {
        return (
          <SolidButton
            buttonText={<FormattedMessage id={"profile.following_text"} />}
            disabled={false}
            className={styles.button}
            buttonStyle={{
              backgroundColor: "rgba(0,0,0,0)",
            }}
            onClick={onUnFollowThisProfileUser}
          />
        );
      } else {
        return (
          <SolidButton
            buttonText={<FormattedMessage id={"profile.follow_text"} />}
            disabled={false}
            className={styles.button}
            buttonStyle={{
              backgroundColor: BLUE700,
              color: WHITE100,
              border: "none",
            }}
            onClick={onFollowThisProfileUser}
          />
        );
      }
    }
  };

  private readonly renderDMButton = () => {
    return (
      <div className={styles.dmButton} onClick={this.props.onDMClick}>
        <IconWrapper
          icon={PACKS.PROFILE.TALK_MESSAGE}
          className={styles.icon}
        />
      </div>
    );
  };

  private readonly getHeaderButton = () => {
    const { currentUser, user, isMine } = this.props;

    if (user && currentUser) {
      if (!isMine) {
        return (
          <div className={styles.toRight}>
            {this.renderDMButton()}
            {this.getUserFollowUnFollowButton()}
          </div>
        );
      } else {
        return (
          <Link
            className={styles.editButton}
            to={new VingleURL.AccountEditProfile().toString()}
          >
            <FormattedMessage id={"profile.edit_text"} />
          </Link>
        );
      }
    } else {
      return null;
    }
  };

  private readonly getCoverImageDOM = () => {
    const { user } = this.props;

    let imageSrc;
    if (this.state.cover_image) {
      imageSrc = this.state.cover_image;
    } else if (user && user.cover_image_urls) {
      imageSrc = user.cover_image_urls.l;
    }

    if (imageSrc) {
      return (
        <div
          style={{ backgroundImage: `url("${imageSrc}")` }}
          className={styles.coverImage}
        />
      );
    }

    return <div className={styles.placeHolderImage} />;
  };

  private readonly fullscreenInfiniteScrollLoadMore = () => {
    if (this.state.dialogType) {
      this.props.onFullScreenInfiniteLoad(this.state.dialogType);
    }
  };

  private readonly renderUserListDialog = () => {
    const { isDialogOpen, dialogType } = this.state;
    const { user, scoreState, userProfile } = this.props;

    let count = 0;
    let items: ReadonlyArray<Vingle.User.IListUser> = [];
    let after = null;
    const onFollow: (user: Vingle.User.IUser) => void = this.props
      .onFollowUserOfList;
    let emptyMessageId = "blank";

    switch (dialogType) {
      case DIALOG_TYPES.FOLLOWEE:
        count = user && user.followee_count ? user.followee_count : 0;
        if (scoreState.followees) {
          items = scoreState.followees.data;
          after = scoreState.followees.paging.after;
          emptyMessageId = "user_list.no_followee";
        }
        break;
      case DIALOG_TYPES.FOLLOWER:
        count = user && user.follower_count ? user.follower_count : 0;
        if (scoreState.followers) {
          items = scoreState.followers.data;
          after = scoreState.followers.paging.after;
          emptyMessageId = "user_list.no_follower";
        }
        break;
      case DIALOG_TYPES.BOOSTER:
        count = user && user.boost_count ? user.boost_count : 0;
        if (scoreState.boosters) {
          items = scoreState.boosters.data;
          after = scoreState.boosters.paging.after;
          emptyMessageId = "user_list.no_booster";
        }
        break;
    }

    if (!onFollow) return null;
    const listTitle =
      dialogType === DIALOG_TYPES.BOOSTER ? (
        <ListTitle titleId={dialogType} />
      ) : (
        <ListTitle titleId={dialogType} count={count} />
      );
    const firstLoading = userProfile.isLoading && !after;
    return (
      <FullscreenDialog
        open={isDialogOpen}
        title={listTitle}
        onClose={this.handleRequestDialogClose}
        infiniteOptions={{
          itemLength: items.length,
          loader: <PulseLoadingDot />,
          paging: { after },
          loadMore: this.fullscreenInfiniteScrollLoadMore,
        }}
      >
        {!firstLoading ? (
          <UserList
            currentUser={this.props.currentUser}
            users={items}
            onFollowClick={onFollow}
            type={UserListType.List}
            emptyElement={
              <EmptyMessage
                messageId={emptyMessageId}
                style={{ marginTop: "150px" }}
              />
            }
          />
        ) : null}
      </FullscreenDialog>
    );
  };

  private readonly handleChangePhoto = (type?: string) => {
    const input = this.fileInput.current;
    if (input) {
      input.click();
      if (type) input.setAttribute("data-type", type);
      else input.removeAttribute("data-type");
    }
  };

  private readonly handleChangeImage = (
    e: React.FormEvent<HTMLInputElement>,
  ) => {
    const { onChangeUserProfile, currentUser } = this.props;
    if (!currentUser) return;
    const target = e.target as HTMLInputElement;
    const filledData: Vingle.User.IUserForm = {
      bio: currentUser.bio as string,
      email: currentUser.email as string,
    };

    if (onChangeUserProfile && target.files && target.files[0]) {
      const key = target.getAttribute("data-type")
        ? "cover_image"
        : "profile_image";

      const reader = new FileReader();

      reader.onload = event => {
        this.setState({
          ...this.state,
          [key]: (event.target as any).result,
        });
      };
      reader.readAsDataURL(target.files[0]);

      onChangeUserProfile({
        updateFields: filledData,
        [key]: target.files[0],
      });
      target.setAttribute("data-type", "");
    }
    target.value = "";
  };

  private readonly openDialog = (dialogType: DIALOG_TYPES) => {
    this.props.onInitialUserListLoad(dialogType);
    this.setState({
      isDialogOpen: true,
      dialogType,
    });
  };

  private readonly handleRequestFolloweeDialogOpen = () => {
    this.openDialog(DIALOG_TYPES.FOLLOWEE);
  };

  private readonly handleRequestFollowerDialogOpen = () => {
    this.openDialog(DIALOG_TYPES.FOLLOWER);
  };

  private readonly handleRequestBoosterDialogOpen = () => {
    this.openDialog(DIALOG_TYPES.BOOSTER);
  };

  private readonly handleRequestDialogClose = () => {
    this.setState({
      isDialogOpen: false,
      dialogType: null,
    });
  };

  private readonly handleOpenDropdown = (anchor: any) => {
    this.setState({
      isOpenDropdownMenu: true,
      anchorOfDropdownMenu: anchor,
    });
  };

  private readonly handleCloseDropdown = () => {
    this.setState({ isOpenDropdownMenu: false, anchorOfDropdownMenu: null });
  };
}

export default ProfileDefaultHeader;
